/**
 * PredictorPickerButton — Feb 2026 top-bar CTA.
 *
 * Single always-shining outlined button that opens a small dropdown
 * offering the two live predictors: MHT-CET (engineering) and NEET
 * (medical). Replaces the older single "NEET predictor" pill so both
 * exam paths get equal prominence — students self-route without leaving
 * the top bar.
 *
 * Style notes:
 *   • Permanent gradient outline that "shines" — a keyframe (defined in
 *     App.css :: @keyframes predictor-shine) rotates the conic gradient
 *     mask so the outline looks like it's catching light. Tastefully
 *     slow (4s) so it reads as premium, not disco.
 *   • Trigger uses button, not a Link — so we can open a menu without
 *     kicking users out of the current page.
 *   • Options are Links to the existing public predictor pages (no auth
 *     needed to preview).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Cpu, HeartPulse, Sparkles } from "lucide-react";

const OPTIONS = [
  {
    key: "engineering",
    label: "Engineering predictor",
    sub: "MHT-CET · Maharashtra CAP",
    to: "/cet/instant",
    Icon: Cpu,
    testid: "predictor-pick-engineering",
    accent: "bg-amber-50 text-amber-800 border-amber-200",
  },
  {
    key: "neet",
    label: "NEET predictor",
    sub: "MCC + State DME · MBBS/BDS",
    to: "/neet/instant",
    Icon: HeartPulse,
    testid: "predictor-pick-neet",
    accent: "bg-emerald-50 text-[#0F6E56] border-emerald-200",
  },
];

export default function PredictorPickerButton() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      {/* Shining-outline wrapper: an outer div paints the animated
          conic gradient; the inner button holds the actual content and
          masks the middle to keep only a 2px "ring" of gradient visible. */}
      <div
        className="relative rounded-full p-[2px] predictor-shine-ring"
        data-testid="nav-predictor-shine-wrap"
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="menu"
          className="relative inline-flex items-center h-9 px-3.5 rounded-full text-[13px] font-bold bg-white text-slate-900 hover:bg-slate-50 transition-colors gap-1.5"
          data-testid="nav-predictor-picker"
          title="Open the exam predictor — choose engineering or NEET"
        >
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          <span>Try predictor</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
          role="menu"
          aria-label="Exam predictors"
          data-testid="nav-predictor-panel"
        >
          <p className="px-3 pt-3 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
            Choose your exam
          </p>
          <ul className="pb-2">
            {OPTIONS.map(({ key, label, sub, to, Icon, testid, accent }) => (
              <li key={key}>
                <Link
                  to={to}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                  data-testid={testid}
                >
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${accent}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] font-semibold text-slate-900 truncate">{label}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{sub}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/60">
            <p className="text-[11px] text-slate-500 leading-snug">
              No signup to preview — see your shortlist first, decide later.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
